import React from 'react'
import { Box } from '@mui/material'
import { AmchartSeeker } from '@ui-seeker/core'

import { isAnalyzerLoading } from 'Pages/AnalyzersV2/@signals'
import { POSITION_MAIN_COLOR } from 'Pages/AnalyzersV2/@constants'
import { buildMarketBandData, findMainKey } from 'Pages/AnalyzersV2/@components/MarketBandChart/@utils'
import RankStrip from 'Pages/AnalyzersV2/@components/MarketBandChart/RankStrip'
import ChartFullScreen from 'Pages/AnalyzersV2/@components/ChartFullScreen'
import { AppCardTitle, AppResultsSkeleton } from '@components'
import { dataColors } from '@palette'

const BAND_COLOR = dataColors.slate400
const AVG_COLOR = dataColors.slate500
const TITLE = 'Position over time'
const ICON = 'chart-area'


export default function MarketBandChart({ marketChartData, totalBrands }) {
	if (isAnalyzerLoading.value) return <AppResultsSkeleton chart height={500}/>
	if (!marketChartData?.length) return <></>

	const mainKey = findMainKey(marketChartData)

	if (!mainKey) return <></>

	const data = buildMarketBandData(marketChartData, mainKey)
	const subtitle = `${mainKey} vs. competitor price band, by pickup date`

	return <Box>
		<ChartFullScreen rootID="MarketBandChart" height={360} title={TITLE} subtitle={subtitle} icon={ICON} renderChart={viewProps => <AmchartSeeker
			title={<AppCardTitle icon={ICON} title={TITLE} subtitle={subtitle}/>}
			{...viewProps}
			data={data}
			// Pickup dates are not consecutive (every 2-5 days further out), so time
			// gaps must not break the lines; only missing values do.
			series={[{
				// Competitor min–max band, drawn first so the lines sit on top of it.
				name: 'Competitor range',
				yField: 'maxPrice',
				xField: 'date',
				openValueYField: 'minPrice',
				color: BAND_COLOR,
				connect: false,
				tension: 1,
				// The pickup dates of a report are days apart: the line joins them and breaks only on a date without a rate
				autoGapCount: Infinity,
				fills: { fillOpacity: .28, visible: true },
				strokes: { strokeOpacity: .6, strokeWidth: 1 },
				bullets: { hidden: true },
				tooltip: { labelText: 'Competitor range: {openValueY} - {valueY}' },
			}, {
				name: 'Market average',
				yField: 'avgPrice',
				xField: 'date',
				color: AVG_COLOR,
				connect: false,
				tension: 1,
				// The pickup dates of a report are days apart: the line joins them and breaks only on a date without a rate
				autoGapCount: Infinity,
				strokes: { strokeWidth: 1.5, strokeDasharray: [5, 4] },
				bullets: { hidden: true },
			}, {
				name: mainKey,
				yField: 'main',
				xField: 'date',
				color: POSITION_MAIN_COLOR,
				// One line across a day without a rate: the dots say which days have one
				connect: true,
				tension: 1,
				// The pickup dates of a report are days apart: the line joins them and breaks only on a date without a rate
				autoGapCount: Infinity,
				strokes: { strokeWidth: 2.5 },
			}]}
			// The chart rewrites the dates of the rows it is given: the strip gets its own copy
			extraFooter={<RankStrip rows={buildMarketBandData(marketChartData, mainKey)} totalBrands={totalBrands}/>}
			chartProps={{
				showBullets: true,
				enabledPDF: false,
				numberFormat: "#,###.00",
				dateFormatReceived: 'yyyyMMdd',
				// No scrollbars: two months of dates fit, and the full screen gives the room
				scrollbarX: { hidden: true },
				xAxis: {
					categoryField: 'date',
					maxDeviation: 0.3,
					isDateAxis: true,
					dateFormat: 'dd MMM yyyy (EEE)',
					baseInterval: { timeUnit: 'day', count: 1 },
				// Days on the axis, never "2026 W43": the chart labels weeks with that format, so it only gets day steps
				gridIntervals: [1, 2, 3, 5, 7, 14].map(count => ({ timeUnit: 'day', count })).concat({ timeUnit: 'month', count: 1 }),
				},
				yAxis: { title: 'Prices', min: 0, extraMax: .1 },
			}}
		/>}/>
	</Box>
}
